/* global analytics */
(function () {
  // eslint-disable-next-line no-console
  console.log(globalThis.CDP_ANALYTICS_SETTINGS?.edge_domain);
  if (typeof globalThis.window === 'undefined') return;

  // ---------------------------
  // DOM Ready helper
  // ---------------------------
  function onDomReady(fn) {
    if (document.readyState === 'complete' || document.readyState === 'interactive')
      setTimeout(fn, 0);
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // ---------------------------
  // Small utils
  // ---------------------------
  function isEmpty(val) {
    return val === null || val === undefined || (typeof val === 'string' && val.trim() === '');
  }

  function prune(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const out = Array.isArray(obj) ? [] : {};
    for (const k of Object.keys(obj)) {
      let v = obj[k];
      if (v && typeof v === 'object') v = prune(v);
      let omit = isEmpty(v);
      if (!omit && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)
        omit = true;
      if (!omit) out[k] = v;
    }

    return out;
  }

  // normalize host (strip www.)
  function baseHost(loc) {
    try {
      const h = (loc && loc.hostname) || '';
      return h.replace(/^www\./i, '').toLowerCase();
    } catch {
      return '';
    }
  }

  // ---------------------------
  // Edge-domain based source lookup (utm_source mapping)
  // ---------------------------
  function edgeDomainHost() {
    const raw = (globalThis.CDP_ANALYTICS_SETTINGS?.edge_domain || '').trim();
    if (!raw) return '';
    return raw.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  }

  function srcLookupUrl(source) {
    const host = edgeDomainHost();
    // eslint-disable-next-line no-console
    console.log(host);
    if (!host) return '';
    // NOTE: adjust path if your endpoint differs
    return `https://${host}/getsrc?s=${encodeURIComponent(String(source ?? ''))}`;
  }

  const _srcCache = new Map();

  async function chkSource(source) {
    const src = String(source ?? '').trim();
    if (!src) return src;

    if (_srcCache.has(src)) return _srcCache.get(src);

    const url = srcLookupUrl(src);
    if (!url) return src;

    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 600);

    try {
      const res = await fetch(url, { signal: controller.signal, credentials: 'omit' });
      if (!res.ok) return src;
      // eslint-disable-next-line unicorn/no-await-expression-member
      const text = (await res.text()).trim();
      const mapped = text || src;
      _srcCache.set(src, mapped);
      return mapped;
    } catch {
      return src;
    } finally {
      clearTimeout(t);
    }
  }

  // ---------------------------
  // GA helpers
  // ---------------------------
  function parseGAClientId() {
    try {
      const m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (!m) return;
      const val = decodeURIComponent(m[1]);
      const parts = val.split('.');
      if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
      const m2 = val.match(/(\d+\.\d+)/);
      return m2 ? m2[1] : undefined;
    } catch {}
  }

  function buildGaOnly() {
    const clientId = parseGAClientId();
    if (!clientId) return null;
    // eslint-disable-next-line camelcase
    return prune({ client_id: clientId });
  }

  // ---------------------------
  // Source Cleaner (matches your middleware behavior)
  // - unlk* -> utm*
  // - lowercases utm_medium
  // - rewrites browser URL via history.replaceState
  // - returns cleaned { campaign, search, url }
  // ---------------------------
  // eslint-disable-next-line complexity
  async function sourceCleaner() {
    try {
      const l = globalThis.location || {};
      const currentHref = l.href || '';
      const qs = l.search || '';

      const queryParams = new URLSearchParams(qs);
      const newURL = new URL(currentHref);

      const campaign = {};

      // medium: utm_medium > unlkmed
      if (queryParams.has('utm_medium')) {
        campaign.medium = String(queryParams.get('utm_medium') || '').toLowerCase();
        queryParams.set('utm_medium', campaign.medium);
      } else if (queryParams.has('unlkmed')) {
        campaign.medium = String(queryParams.get('unlkmed') || '').toLowerCase();
        queryParams.set('utm_medium', campaign.medium);
      }

      // unlkcamp -> utm_campaign
      if (queryParams.has('unlkcamp') && !queryParams.has('utm_campaign')) {
        campaign.name = queryParams.get('unlkcamp');
        queryParams.set('utm_campaign', campaign.name);
      }

      // unlksrcid -> utm_id
      if (queryParams.has('unlksrcid') && !queryParams.has('utm_id')) {
        campaign.id = queryParams.get('unlksrcid');
        queryParams.set('utm_id', campaign.id);
      }

      // unlkcpy -> utm_content
      if (queryParams.has('unlkcpy') && !queryParams.has('utm_content')) {
        campaign.content = queryParams.get('unlkcpy');
        queryParams.set('utm_content', campaign.content);
      }

      // unlkterm -> utm_term
      if (queryParams.has('unlkterm') && !queryParams.has('utm_term')) {
        campaign.term = queryParams.get('unlkterm');
        queryParams.set('utm_term', campaign.term);
      }

      // source: utm_source > unlksrc
      let origSrc = null;
      if (queryParams.has('utm_source')) origSrc = queryParams.get('utm_source');
      else if (queryParams.has('unlksrc')) origSrc = queryParams.get('unlksrc');

      if (origSrc !== null) {
        campaign.source = await chkSource(origSrc);
        queryParams.set('utm_source', campaign.source);
      }

      const newQuery = queryParams.toString();
      const newSearch = newQuery ? `?${newQuery}` : '';
      const cleanedHref = `${newURL.protocol}//${newURL.hostname}${newURL.pathname}${newSearch}`;

      // Replace only if changed
      if ((qs || '') !== newSearch) {
        // Keep it middleware-like: replace query string (no reload)
        // eslint-disable-next-line no-restricted-globals
        history.replaceState(null, null, newSearch || newURL.pathname);
      }

      return { campaign, search: newSearch, url: cleanedHref };
    } catch {
      return null;
    }
  }

  // ---------------------------
  // Page payload and enrichment
  // ---------------------------
  function pageNameFromContext() {
    const body = document.body || {};
    if ((body.className || '').includes('error404')) return 'Page Not Found';
    const path = (globalThis.location && globalThis.location.pathname) || '/';
    if (path === '/' || path === '') return 'Home';
    try {
      const og = document.querySelector('meta[property="og:title"]');
      if (og && og.content) return og.content;
    } catch {}

    return document.title || 'Page';
  }

  function buildPagePayload() {
    const l = globalThis.location || {};
    const d = document || {};
    const name = pageNameFromContext();

    let props = {
      path: l.pathname || undefined,
      referrer: d.referrer || '',
      search: l.search || '',
      title: d.title || undefined,
      url: l.href || undefined,
      name
    };

    // Only keep GA session
    const ga4 = buildGaOnly();
    // eslint-disable-next-line camelcase
    if (ga4) props.ga4_session = ga4;

    props = prune(props);
    return { name, properties: props };
  }

  function enrichProps(baseProps) {
    baseProps = baseProps || {};
    const ga4 = buildGaOnly();
    // eslint-disable-next-line camelcase
    if (ga4) baseProps.ga4_session = ga4;
    return prune(baseProps);
  }

  // ---------------------------
  // Page fire
  // ---------------------------
  function fireRobustPage() {
    const payload = buildPagePayload();
    try {
      const p = analytics.page(payload.name, payload.properties);
      if (p && typeof p.then === 'function') {
        p.then(() => {
          try {
            // eslint-disable-next-line no-console
            console.info('[CDPAnalytics] page sent', payload);
          } catch {}
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[CDPAnalytics] analytics.page error', error);
    }
  }

  // ---------------------------
  // Link tracking
  // ---------------------------
  function bindTrackLink(root) {
    try {
      if (!globalThis.analytics || typeof analytics.trackLink !== 'function') return;
      const links = (root || document).querySelectorAll('a[href]');

      analytics.trackLink(links, 'Link Clicked', (el) => {
        const href = el.getAttribute('href') || el.href || '';
        const lower = href.toLowerCase();
        const isTel = lower.indexOf('tel:') === 0;
        const isMail = lower.indexOf('mailto:') === 0;

        let isExternal = false;
        try {
          const u = new URL(href, globalThis.location.href);
          const originHost = baseHost(u);
          const currentHost = baseHost(globalThis.location);
          isExternal = u.origin !== globalThis.location.origin && originHost !== currentHost;
        } catch {}

        let props = {
          // eslint-disable-next-line camelcase
          non_interactive: false,
          label: (el.getAttribute('data-track-text') || el.textContent || '').trim().slice(0, 300),
          // eslint-disable-next-line camelcase
          link_url: href,
          // eslint-disable-next-line camelcase
          link_type: isTel ? 'telephone' : isMail ? 'email' : isExternal ? 'external' : 'internal'
        };

        // eslint-disable-next-line camelcase
        if (isTel) props.number_dialed = href.replace(/^tel:/i, '');

        props = enrichProps(props);
        return prune(props);
      });

      // eslint-disable-next-line no-console
      console.info('[CDPAnalytics] trackLink bound to', links.length, 'links');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[CDPAnalytics] trackLink binding error', error);
    }
  }

  // Gravity Forms re-renders form markup (AJAX submit, page nav, conditional
  // logic), so links inside it (e.g. consent field copy) need rebinding each time.
  function bindGformTrackLink() {
    if (!globalThis.jQuery) return;

    globalThis.jQuery(document).on('gform_post_render', (_e, formId) => {
      const formEl = document.getElementById(`gform_${formId}`);
      if (formEl) bindTrackLink(formEl);
    });
  }

  // ---------------------------
  // Wrap analytics.track to enrich all manual calls
  // ---------------------------
  function wrapGlobalTrack() {
    if (!analytics || typeof analytics.track !== 'function') return;
    const _track = analytics.track.bind(analytics);

    analytics.track = function (event, properties, options, callback) {
      try {
        properties = properties || {};
        if (event === 'Link Clicked') {
          // eslint-disable-next-line camelcase
          properties.non_interactive =
            typeof properties.non_interactive === 'boolean' ? properties.non_interactive : false;
          properties.label = properties.label || properties.text || '';
          // eslint-disable-next-line camelcase
          properties.link_url = properties.link_url || properties.href || '';

          if (properties.link_url) {
            const ln = String(properties.link_url).toLowerCase();
            const isTel = ln.indexOf('tel:') === 0;
            const isMail = ln.indexOf('mailto:') === 0;

            if (!properties.link_type) {
              // eslint-disable-next-line max-depth
              try {
                // eslint-disable-next-line camelcase,max-depth
                if (isTel) properties.link_type = 'telephone';
                // eslint-disable-next-line camelcase
                else if (isMail) properties.link_type = 'email';
                else {
                  const u = new URL(properties.link_url, globalThis.location.href);
                  const originHost = baseHost(u);
                  const currentHost = baseHost(globalThis.location);
                  // eslint-disable-next-line camelcase
                  properties.link_type = originHost === currentHost ? 'internal' : 'external';
                }
              } catch {
                // eslint-disable-next-line camelcase
                properties.link_type = 'external';
              }
            }

            if (isTel && !properties.number_dialed)
              // eslint-disable-next-line camelcase
              properties.number_dialed = properties.link_url.replace(/^tel:/i, '');
          }
        }

        properties = enrichProps(properties);
        properties = prune(properties);
      } catch {}

      return _track(event, properties, options, callback);
    };

    // eslint-disable-next-line no-console
    console.info('[CDPAnalytics] analytics.track wrapper installed');
  }

  // ---------------------------
  // Scroll Depth tracking (25/50/75/100) — only after actual scroll
  // ---------------------------
  function installScrollDepth() {
    try {
      const marks = [25, 50, 75, 100];
      const fired = {};
      let maxSeen = 0;

      function computePct() {
        const doc = document.documentElement;
        const { body } = document;
        const scrollTop = window.pageYOffset || doc.scrollTop || body.scrollTop || 0;
        const viewport = window.innerHeight || doc.clientHeight || 0;
        const full = Math.max(
          doc.scrollHeight,
          body.scrollHeight,
          doc.offsetHeight,
          body.offsetHeight,
          doc.clientHeight
        );
        if (!full) return 0;
        return Math.min(100, Math.round(((scrollTop + viewport) / full) * 100));
      }

      function onScroll() {
        const pct = computePct();
        if (pct <= 25 && maxSeen <= 25) {
          maxSeen = pct;
          return;
        }

        maxSeen = Math.max(maxSeen, pct);

        for (const m of marks) {
          if (!fired[m] && pct >= m) {
            fired[m] = true;

            analytics.track(
              'Scroll Depth',
              prune(
                enrichProps({
                  // eslint-disable-next-line camelcase
                  non_interactive: true,
                  percent: m,
                  path: globalThis.location.pathname,
                  title: document.title || undefined,
                  url: globalThis.location.href
                })
              )
            );

            if (m === 100) window.removeEventListener('scroll', throttled);
          }
        }
      }

      function throttle(fn, wait) {
        let last = 0;
        let t;
        // eslint-disable-next-line func-names
        return function throttled() {
          const now = Date.now();
          if (now - last >= wait) {
            last = now;
            fn();
          } else {
            clearTimeout(t);
            t = setTimeout(
              () => {
                last = Date.now();
                fn();
              },
              wait - (now - last)
            );
          }
        };
      }

      const throttled = throttle(onScroll, 400);
      window.addEventListener('scroll', throttled, { passive: true });
      // eslint-disable-next-line no-console
      console.info('[CDPAnalytics] Scroll Depth tracking installed');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[CDPAnalytics] Scroll Depth error', error);
    }
  }

  // ---------------------------
  // FAQ Clicked — click-only
  // ---------------------------
  function bindFAQTracking() {
    try {
      document.addEventListener(
        'click',
        (e) => {
          let btn = e.target;
          while (btn && !(btn.classList && btn.classList.contains('accordion-button'))) {
            btn = btn.parentElement;
          }

          if (!btn) return;

          const header = btn.closest ? btn.closest('.accordion-header') : null;
          // eslint-disable-next-line camelcase
          const faq_id = (header && header.id) || undefined;
          const text = (btn.textContent || '').trim().slice(0, 300);

          analytics.track(
            'FAQ Clicked',
            prune(
              enrichProps({
                // eslint-disable-next-line camelcase
                text_clicked: text,
                // eslint-disable-next-line camelcase
                faq_id,
                path: globalThis.location.pathname,
                url: globalThis.location.href,
                title: document.title,
                // eslint-disable-next-line camelcase
                non_interactive: false
              })
            )
          );
        },
        true
      );

      // eslint-disable-next-line no-console
      console.info('[CDPAnalytics] FAQ click-only tracking enabled');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[CDPAnalytics] FAQ tracking error', error);
    }
  }

  // ---------------------------
  // Video Tracking (YouTube & Vimeo in Bootstrap modals)
  // ---------------------------
  function setupVideoTracking() {
    try {
      // Track active video state per modal: { provider, player, videoId, started, completed }
      const active = {};

      function mmss(totalSeconds) {
        try {
          totalSeconds = Math.max(0, Math.round(totalSeconds || 0));
          const m = Math.floor(totalSeconds / 60);
          const s = totalSeconds % 60;
          return `${m}:${s < 10 ? `0${s}` : s}`;
        } catch {
          return String(totalSeconds || 0);
        }
      }

      function ytBaseProps(e, videoId, modalSel) {
        return {
          provider: 'youtube',
          // eslint-disable-next-line camelcase
          video_id: videoId,
          // eslint-disable-next-line camelcase
          video_title: e.target.getVideoData?.()?.title || undefined,
          // eslint-disable-next-line camelcase
          video_duration: mmss(e.target.getDuration?.() || 0),
          // eslint-disable-next-line camelcase
          modal_target: modalSel,
          // eslint-disable-next-line no-restricted-globals
          path: location.pathname,
          // eslint-disable-next-line no-restricted-globals
          url: location.href,
          title: document.title,
          // eslint-disable-next-line camelcase
          non_interactive: false
        };
      }

      function bindYouTube(modal, player, videoId) {
        const { id } = modal;
        const modalSel = `#${id}`;
        active[id] = { provider: 'youtube', player, videoId, started: false, completed: false };

        try {
          player.addEventListener('onStateChange', (e) => {
            const state = active[id];
            if (!state) return;

            if (e.data === 1 && !state.started) {
              state.started = true;
              try {
                analytics.track(
                  'Video Started',
                  prune(enrichProps(ytBaseProps(e, videoId, modalSel)))
                );
              } catch {}
            } else if ((e.data === 0 || e.data === 2) && !state.completed) {
              state.completed = true;
              try {
                const props = ytBaseProps(e, videoId, modalSel);
                // eslint-disable-next-line camelcase
                props.video_current_time = mmss(e.target.getCurrentTime?.() || 0);
                props.reason = e.data === 0 ? 'ended' : 'pause';
                analytics.track('Video Ended', prune(enrichProps(props)));
              } catch {}
            }
          });
        } catch {}
      }

      function bindVimeo(modal, player) {
        const { id } = modal;
        const modalSel = `#${id}`;
        active[id] = { provider: 'vimeo', player, started: false, completed: false };

        async function sendStart() {
          const state = active[id];
          if (!state || state.started) return;
          state.started = true;
          try {
            const [title, duration] = await Promise.all([
              player.getVideoTitle(),
              player.getDuration()
            ]);
            analytics.track(
              'Video Started',
              prune(
                enrichProps({
                  provider: 'vimeo',
                  // eslint-disable-next-line camelcase
                  video_title: title || undefined,
                  // eslint-disable-next-line camelcase
                  video_duration: mmss(duration || 0),
                  // eslint-disable-next-line camelcase
                  modal_target: modalSel,
                  path: globalThis.location.pathname,
                  url: globalThis.location.href,
                  title: document.title,
                  // eslint-disable-next-line camelcase
                  non_interactive: false
                })
              )
            );
          } catch {}
        }

        async function sendComplete(reason, secondsMaybe) {
          const state = active[id];
          if (!state || state.completed) return;
          state.completed = true;
          try {
            const [title, duration, current] = await Promise.all([
              player.getVideoTitle(),
              player.getDuration(),
              typeof secondsMaybe === 'number'
                ? Promise.resolve(secondsMaybe)
                : player.getCurrentTime()
            ]);
            analytics.track(
              'Video Ended',
              prune(
                enrichProps({
                  provider: 'vimeo',
                  // eslint-disable-next-line camelcase
                  video_title: title || undefined,
                  // eslint-disable-next-line camelcase
                  video_duration: mmss(duration || 0),
                  // eslint-disable-next-line camelcase
                  video_current_time: mmss(current || 0),
                  // eslint-disable-next-line camelcase
                  modal_target: modalSel,
                  reason,
                  path: globalThis.location.pathname,
                  url: globalThis.location.href,
                  title: document.title,
                  // eslint-disable-next-line camelcase
                  non_interactive: false
                })
              )
            );
          } catch {}
        }

        player.on('play', () => {
          sendStart();
        });
        player.on('pause', (data) => {
          sendComplete(
            'pause',
            data && typeof data.seconds === 'number' ? data.seconds : undefined
          );
        });
        player.on('ended', (data) => {
          sendComplete(
            'ended',
            data && typeof data.seconds === 'number' ? data.seconds : undefined
          );
        });
      }

      // VideoModal dispatches this event after the player is created and ready
      document.addEventListener('videomodal.ready', (ev) => {
        const modal = ev.target;
        if (!modal || !modal.id) return;
        const { provider, player, videoId } = ev.detail || {};
        if (!player) return;

        if (provider === 'youtube') bindYouTube(modal, player, videoId);
        if (provider === 'vimeo') bindVimeo(modal, player);
      });

      // Send "Video Ended" when modal closes (before VideoModal destroys the player)
      document.addEventListener(
        'hide.bs.modal',
        (ev) => {
          const modal = ev.target;
          if (!modal || !modal.id) return;
          const { id } = modal;
          const state = active[id];
          if (!state || state.completed) return;

          state.completed = true;
          const modalSel = `#${id}`;

          if (state.provider === 'youtube') {
            try {
              const p = state.player;
              const cur = p.getCurrentTime?.() || 0;
              const d = p.getDuration?.() || 0;
              const t = p.getVideoData?.()?.title || '';
              analytics.track(
                'Video Ended',
                prune(
                  enrichProps({
                    provider: 'youtube',
                    // eslint-disable-next-line camelcase
                    video_id: state.videoId,
                    // eslint-disable-next-line camelcase
                    video_title: t || undefined,
                    // eslint-disable-next-line camelcase
                    video_duration: mmss(d || 0),
                    // eslint-disable-next-line camelcase
                    video_current_time: mmss(cur || 0),
                    // eslint-disable-next-line camelcase
                    modal_target: modalSel,
                    reason: 'hide',
                    // eslint-disable-next-line no-restricted-globals
                    path: location.pathname,
                    // eslint-disable-next-line no-restricted-globals
                    url: location.href,
                    title: document.title,
                    // eslint-disable-next-line camelcase
                    non_interactive: false
                  })
                )
              );
            } catch {}
          }

          if (state.provider === 'vimeo') {
            (async function () {
              try {
                const p = state.player;
                const sec = await p.getCurrentTime().catch(() => 0);
                const [title, duration] = await Promise.all([p.getVideoTitle(), p.getDuration()]);
                analytics.track(
                  'Video Ended',
                  prune(
                    enrichProps({
                      provider: 'vimeo',
                      // eslint-disable-next-line camelcase
                      video_title: title || undefined,
                      // eslint-disable-next-line camelcase
                      video_duration: mmss(duration || 0),
                      // eslint-disable-next-line camelcase
                      video_current_time: mmss(sec || 0),
                      // eslint-disable-next-line camelcase
                      modal_target: modalSel,
                      reason: 'hide',
                      path: globalThis.location.pathname,
                      url: globalThis.location.href,
                      title: document.title,
                      // eslint-disable-next-line camelcase
                      non_interactive: false
                    })
                  )
                );
              } catch {}
            })();
          }
        },
        true
      );

      // eslint-disable-next-line no-console
      console.info('[CDPAnalytics] Video tracking initialized');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[CDPAnalytics] Video tracking error', error);
    }
  }

  // ---------------------------
  // Gravity Forms Tracking (progress, errors, submission)
  // ---------------------------
  function setupGravityFormsTracking() {
    try {
      function getFormName(formEl) {
        if (!formEl) return 'Form';
        // Admin label injected server-side (GF "Admin Label" field in form settings)
        const adminLabel = (formEl.getAttribute('data-admin-label') || '').trim();
        if (adminLabel) return adminLabel;
        return 'Form';
      }

      function getFormType(formEl) {
        if (!formEl) return 'Other';
        const formType = (formEl.getAttribute('data-form-type') || '').trim();
        if (formType) return formType;
        const id = formEl.id || '';
        if (id.includes('search-form')) return 'Search Form';
        return 'Other';
      }

      function isMultiPage(formEl) {
        return formEl ? formEl.querySelectorAll('.gform_page').length > 0 : false;
      }

      // Translates GF's raw page number into the user-visible step count by
      // skipping pages hidden via conditional logic (data-conditional-logic="hidden").
      function getVisibleStep(formEl, pageNumber) {
        const n = Number(pageNumber);
        if (!formEl || !n) return n;

        const pages = formEl.querySelectorAll('.gform_page');
        if (pages.length === 0) return n;

        let visibleStep = 0;
        for (const page of pages) {
          const match = (page.id || '').match(/_(\d+)$/);
          if (!match) continue;
          const pageNum = Number(match[1]);

          const isHidden =
            page.dataset.conditionalLogic === 'hidden' ||
            page.classList.contains('gform_page_visibility_hidden');

          if (!isHidden) visibleStep++;
          if (pageNum === n) return visibleStep || n;
        }

        return visibleStep || n;
      }

      function getPageName(formEl, pageNumber) {
        if (!formEl) return;
        const page = formEl.querySelector(`#gform_page_${formEl.dataset.formid}_${pageNumber}[data-page-name]`)
          || formEl.querySelector('.gform_page[data-page-name]:not([style*="display: none"])');
        return page ? page.getAttribute('data-page-name') || undefined : undefined;
      }

      // GF renders each .gform_page with data-js="page-field-id-{id}" — the ID
      // of the Page-break field that starts it, as set in the form editor.
      // Page 1 (no page-break field precedes it) gets id "0".
      function getPageFieldId(formEl, pageNumber) {
        if (!formEl) return;
        const page = formEl.querySelector(`#gform_page_${formEl.dataset.formid}_${pageNumber}`);
        const match = (page?.dataset.js || '').match(/^page-field-id-(\d+)$/);
        return match ? Number(match[1]) : undefined;
      }

      function getActivePage(formEl) {
        for (const page of formEl.querySelectorAll('.gform_page')) {
          if (getComputedStyle(page).display !== 'none') return page;
        }

        return formEl.querySelector('.gform_body') || formEl;
      }

      // AVM's managed UUID hidden field (avm/sync-fields.php) carries the
      // cssClass 'avm-uuid-field' specifically so it can be located generically
      // here — its GF field ID differs per form, so there's no fixed selector.
      function getUuid(formEl) {
        const input = formEl?.querySelector('.avm-uuid-field input[type="hidden"]');
        return input?.value || '';
      }

      function formatPhoneE164(val) {
        const digits = String(val).replaceAll(/\D/g, '').replace(/^1/, '').slice(0, 10);
        if (digits.length === 10) return `+1${digits}`;
        return val;
      }

      function capturePageData(formEl) {
        const data = {};
        if (!formEl) return data;
        const scope = getActivePage(formEl);
        if (!scope) return data;
        for (const field of scope.querySelectorAll('.gfield')) {
          const label = getFieldLabel(field);
          if (!label) continue;
          const checked = [...field.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked')];

          if (checked.length) { data[label] = checked.map((c) => c.value).join(', '); continue; }
          const inputs = [...field.querySelectorAll(
            'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]), select, textarea'
          )];
          const parts = inputs.map((i) => i.value).filter(Boolean);
          if (!parts.length) continue;
          const isPhone = field.classList.contains('gfield--type-phone') ||
            field.querySelector('input[type="tel"]') !== null;
          data[label] = isPhone ? formatPhoneE164(parts.join(' ')) : parts.join(' ');
        }

        return data;
      }

      function getFieldLabel(field) {
        const gfLabel = field.querySelector('.gfield_label:not(.screen-reader-text)');
        if (gfLabel) {
          const clone = gfLabel.cloneNode(true);
          for (const r of clone.querySelectorAll('.gfield_required, .gfield_required_indicator, .gfield_required_text')) r.remove();
          const text = clone.textContent.trim();
          if (text) return text;
        }

        const floatLabel = field.querySelector('.form-floating > label, label.form-label');
        if (floatLabel) {
          const text = floatLabel.textContent.trim();
          if (text) return text;
        }

        const inp = field.querySelector('input:not([type="hidden"]), select, textarea');

        return inp ? (inp.getAttribute('aria-label') || inp.placeholder || '') : '';
      }

      function getErrorFields(form) {
        return [...form.querySelectorAll('.gfield_error')]
          .map((gfield) => {
            const label = getFieldLabel(gfield);
            if (!label) return null;
            const message = gfield.querySelector('.validation_message, .gfield-inline-error')?.textContent?.trim() || undefined;
            return message ? { label, message } : { label };
          })
          .filter(Boolean);
      }

      function handlePostRender(formId, currentPage) {
        const form = document.getElementById(`gform_${formId}`);
        if (!form) return;

        const wrapper = form.closest('.gform_wrapper');
        const formName = getFormName(form);
        const formType = getFormType(form);

        // Capture form data for handleFormSuccess (form is gone after confirmation)
        const emailInput = form.querySelector('input[type="email"]');
        pendingSubmit[String(formId)] = {
          // eslint-disable-next-line camelcase
          form_name: formName,
          // eslint-disable-next-line camelcase
          form_type: formType,
          email: emailInput?.value?.trim() || '',
          // eslint-disable-next-line camelcase
          pre_application_flow_uuid: getUuid(form) || ''
        };
        const hasErrors =
          wrapper?.classList.contains('gform_validation_error') ||
          form.classList.contains('gform_validation_error');
        // --- Form Error ---
        if (hasErrors) {
          const errorHeading =
            wrapper
              ?.querySelector('.gform_validation_errors .validation_error, .gform_submission_error')
              ?.textContent?.trim() || '';

          const errorFields = getErrorFields(form);

          const isMultiPageStep = isMultiPage(form) && currentPage;
          analytics.track(
            'Form Error',
            prune(
              enrichProps({
                // eslint-disable-next-line camelcase
                form_id: String(formId),
                // eslint-disable-next-line camelcase
                form_name: formName,
                // eslint-disable-next-line camelcase
                form_type: formType,
                // eslint-disable-next-line camelcase
                error_name: errorHeading || undefined,
                // eslint-disable-next-line camelcase
                error_fields: errorFields.length > 0 ? JSON.stringify(errorFields) : undefined,
                // eslint-disable-next-line camelcase
                form_step: isMultiPageStep
                  ? getVisibleStep(form, Number(currentPage))
                  : undefined,
                // eslint-disable-next-line camelcase
                page_name: isMultiPageStep
                  ? getPageName(form, Number(currentPage))
                  : undefined,
                // eslint-disable-next-line camelcase
                pre_application_flow_uuid: getUuid(form) || undefined,
                // eslint-disable-next-line no-restricted-globals
                path: location.pathname,
                // eslint-disable-next-line no-restricted-globals
                url: location.href,
                title: document.title,
                // eslint-disable-next-line camelcase
                non_interactive: false
              })
            )
          );
          return;
        }

        // --- Form Progress (multi-page, page > 1) ---
        if (isMultiPage(form) && Number(currentPage) > 1) {
          analytics.track(
            'Form Progress',
            prune(
              enrichProps({
                // eslint-disable-next-line camelcase
                form_id: String(formId),
                // eslint-disable-next-line camelcase
                form_name: formName,
                // eslint-disable-next-line camelcase
                form_type: formType,
                // eslint-disable-next-line camelcase
                page_id: getPageFieldId(form, Number(currentPage)),
                // eslint-disable-next-line camelcase
                form_step: getVisibleStep(form, Number(currentPage)),
                // eslint-disable-next-line camelcase
                page_name: getPageName(form, Number(currentPage)),
                // eslint-disable-next-line camelcase
                pre_application_flow_uuid: getUuid(form) || undefined,
                // eslint-disable-next-line camelcase
                form_data: pendingFormData[String(formId)] || undefined,
                // eslint-disable-next-line no-restricted-globals
                path: location.pathname,
                // eslint-disable-next-line no-restricted-globals
                url: location.href,
                title: document.title,
                // eslint-disable-next-line camelcase
                non_interactive: false
              })
            )
          );
        }
      }

      // GF 2.9+ CustomEvent: fires on initial render, page advance, validation errors, and confirmation
      document.addEventListener('gform/post_render', (ev) => {
        const { formId, currentPage } = ev.detail || {};
        if (!formId) return;

        // currentPage is undefined after successful submission (confirmation rendered)
        if (currentPage === undefined || currentPage === null) {
          routeConfirmation(formId);
          return;
        }

        handlePostRender(formId, currentPage);
      });

      // Save & Continue button click — treat as a Form Progress step (no form_step)
      document.addEventListener(
        'click',
        (ev) => {
          const btn = ev.target.closest('[data-submission-type="save-continue"], .gform_save_link');
          if (!btn) return;
          const form = btn.closest('form[id^="gform_"]');
          if (!form) return;
          const fid = String(form.dataset.formid || form.id.replace(/^gform_/, ''));
          const formData = JSON.stringify(capturePageData(form));
          // eslint-disable-next-line camelcase
          if (pendingSubmit[fid]) pendingSubmit[fid].form_data = formData;

          analytics.track(
            'Form Progress',
            prune(
              enrichProps({
                // eslint-disable-next-line camelcase
                form_id: fid,
                // eslint-disable-next-line camelcase
                form_name: getFormName(form),
                // eslint-disable-next-line camelcase
                form_type: getFormType(form),
                // eslint-disable-next-line camelcase
                page_name: 'Save Form',
                // eslint-disable-next-line camelcase
                pre_application_flow_uuid: getUuid(form) || undefined,
                // eslint-disable-next-line camelcase
                form_data: formData,
                // eslint-disable-next-line no-restricted-globals
                path: location.pathname,
                // eslint-disable-next-line no-restricted-globals
                url: location.href,
                title: document.title,
                // eslint-disable-next-line camelcase
                non_interactive: false
              })
            )
          );
        },
        true
      );

      // Capture the email the user types into the Save & Continue resume-email input
      document.addEventListener('input', (ev) => {
        const input = ev.target;
        if (!input || !input.matches) return;
        if (!input.matches('input[name="gform_resume_email"], input[name*="resume_email"]')) return;
        const form = input.closest('form[id^="gform_"]');
        if (!form) return;
        const fid = String(form.dataset.formid || form.id.replace(/^gform_/, ''));
        const state = saveFlowState.get(fid) || {};
        state.email = (input.value || '').trim();
        saveFlowState.set(fid, state);
      });

      // Client-side validation error from form.js (disabled Next/Submit tap)
      document.addEventListener('unlock/form/client_error', (ev) => {
        const form = ev.target;
        const { formId, currentPage, trigger } = ev.detail || {};
        if (!form || !formId) return;

        const errorFields = getErrorFields(form);

        analytics.track(
          'Form Error',
          prune(
            enrichProps({
              // eslint-disable-next-line camelcase
              form_id: String(formId),
              // eslint-disable-next-line camelcase
              form_name: getFormName(form),
              // eslint-disable-next-line camelcase
              form_type: getFormType(form),
              // eslint-disable-next-line camelcase
              error_name: 'Required fields missing',
              // eslint-disable-next-line camelcase
              error_fields: errorFields.length > 0 ? JSON.stringify(errorFields) : undefined,
              trigger: trigger || undefined,
              // eslint-disable-next-line camelcase
              form_step: isMultiPage(form) && currentPage
                ? getVisibleStep(form, currentPage)
                : undefined,
              // eslint-disable-next-line camelcase
              page_name: isMultiPage(form) && currentPage
                ? getPageName(form, currentPage)
                : undefined,
              // eslint-disable-next-line camelcase
              pre_application_flow_uuid: getUuid(form) || undefined,
              // eslint-disable-next-line no-restricted-globals
              path: location.pathname,
              // eslint-disable-next-line no-restricted-globals
              url: location.href,
              title: document.title,
              // eslint-disable-next-line camelcase
              non_interactive: false
            })
          )
        );
      });

      // GF 2.9+ CustomEvent: fires specifically on multi-page AJAX page change
      document.addEventListener('gform/ajax/post_page_change', (ev) => {
        const { formId, pageNumber } = ev.detail || {};
        if (!formId || !pageNumber || pageNumber <= 1) return;

        const form = document.getElementById(`gform_${formId}`);
        if (!form) return;

        analytics.track(
          'Form Progress',
          prune(
            enrichProps({
              // eslint-disable-next-line camelcase
              form_id: String(formId),
              // eslint-disable-next-line camelcase
              form_name: getFormName(form),
              // eslint-disable-next-line camelcase
              form_type: getFormType(form),
              // eslint-disable-next-line camelcase
              page_id: getPageFieldId(form, pageNumber),
              // eslint-disable-next-line camelcase
              form_step: getVisibleStep(form, pageNumber),
              // eslint-disable-next-line camelcase
              page_name: getPageName(form, pageNumber),
              // eslint-disable-next-line camelcase
              pre_application_flow_uuid: getUuid(form) || undefined,
              // eslint-disable-next-line camelcase
              form_data: pendingFormData[String(formId)] || undefined,
              // eslint-disable-next-line no-restricted-globals
              path: location.pathname,
              // eslint-disable-next-line no-restricted-globals
              url: location.href,
              title: document.title,
              // eslint-disable-next-line camelcase
              non_interactive: false
            })
          )
        );
      });

      // --- Form Submit + Form Success (GF 2.9+ and legacy) ---
      // Save-and-continue confirmations are identified by GF's native resume URL
      // (the `gf_token` parameter). GF renders it as either an <a href> or a
      // plain-text URL depending on the confirmation template.
      function findFormContainer(fid) {
        return (
          document.getElementById(`gform_wrapper_${fid}`) ||
          document.getElementById(`gform_confirmation_wrapper_${fid}`)
        );
      }

      function extractResumeLink(container) {
        if (!container) return '';
        const anchor = container.querySelector('a[href*="gf_token="]');
        if (anchor?.href) return anchor.href;
        const match = (container.textContent || '').match(/https?:\/\/\S+[?&]gf_token=\S+/);
        return match ? match[0].replace(/[.,;:)"']+$/, '') : '';
      }

      function routeConfirmation(formId) {
        const fid = String(formId || '');
        if (!fid) return;
        const container = findFormContainer(fid);

        // Save & Continue email-prompt view: GF's native resume-email input is present
        const hasResumeInput = Boolean(
          container?.querySelector(
            'input[name="gform_resume_email"], input[name*="resume_email"]'
          )
        );
        if (hasResumeInput) {
          const state = saveFlowState.get(fid) || {};
          const link = extractResumeLink(container);
          if (link) state.link = link;
          saveFlowState.set(fid, state);
          handleFormSaved(fid, state.link || '');
          return;
        }

        // Save confirmation: either an explicit resume URL is rendered, or we
        // previously saw the save prompt for this form (GF's default "we've
        // emailed you the link" confirmation omits the URL from the DOM).
        const state = saveFlowState.get(fid);
        const resumeLink = extractResumeLink(container) || state?.link || '';
        if (state || resumeLink) {
          handleSavedLinkSent(fid, resumeLink, state?.email || '');
          saveFlowState.delete(fid);
          return;
        }

        handleFormSuccess(fid);
      }

      function getPendingMeta(fid) {
        const pending = pendingSubmit[fid] || {};

        return {
          formName: pending.form_name || 'Form',
          formType: pending.form_type || 'Other',
          formData: pending.form_data,
          preApplicationFlowUuid: pending.pre_application_flow_uuid || ''
        };
      }

      function handleFormSuccess(formId) {
        const fid = String(formId || '');
        if (!fid) return;
        const { formName, formType, formData, preApplicationFlowUuid } = getPendingMeta(fid);
        delete pendingSubmit[fid];

        const baseProps = {
          // eslint-disable-next-line camelcase
          form_id: fid,
          // eslint-disable-next-line camelcase
          form_name: formName,
          // eslint-disable-next-line camelcase
          form_type: formType,
          // eslint-disable-next-line camelcase
          pre_application_flow_uuid: preApplicationFlowUuid || undefined,
          // eslint-disable-next-line no-restricted-globals
          path: location.pathname,
          // eslint-disable-next-line no-restricted-globals
          url: location.href,
          title: document.title,
          // eslint-disable-next-line camelcase
          non_interactive: false
        };

        // eslint-disable-next-line camelcase
        analytics.track('Form Submitted', prune(enrichProps({ ...baseProps, form_data: formData || undefined })));
      }

      function handleFormSaved(formId, resumeLink) {
        const fid = String(formId || '');
        if (!fid) return;
        const { formName, formType, formData, preApplicationFlowUuid } = getPendingMeta(fid);

        analytics.track(
          'Form Saved',
          prune(
            enrichProps({
              // eslint-disable-next-line camelcase
              form_id: fid,
              // eslint-disable-next-line camelcase
              form_name: formName,
              // eslint-disable-next-line camelcase
              form_type: formType,
              // eslint-disable-next-line camelcase
              form_data: formData || undefined,
              // eslint-disable-next-line camelcase
              page_name: 'Save Form',
              // eslint-disable-next-line camelcase
              pre_application_flow_uuid: preApplicationFlowUuid || undefined,
              link: resumeLink || undefined,
              // eslint-disable-next-line no-restricted-globals
              path: location.pathname,
              // eslint-disable-next-line no-restricted-globals
              url: location.href,
              title: document.title,
              // eslint-disable-next-line camelcase
              non_interactive: false
            })
          )
        );
      }

      function handleSavedLinkSent(formId, resumeLink, email) {
        const fid = String(formId || '');
        if (!fid) return;
        const { formName, formType, preApplicationFlowUuid } = getPendingMeta(fid);
        delete pendingSubmit[fid];

        analytics.track(
          'Saved Link Sent',
          prune(
            enrichProps({
              // eslint-disable-next-line camelcase
              form_id: fid,
              // eslint-disable-next-line camelcase
              form_name: formName,
              // eslint-disable-next-line camelcase
              form_type: formType,
              email: email || undefined,
              link: resumeLink || undefined,
              // eslint-disable-next-line camelcase
              pre_application_flow_uuid: preApplicationFlowUuid || undefined,
              // eslint-disable-next-line no-restricted-globals
              path: location.pathname,
              // eslint-disable-next-line no-restricted-globals
              url: location.href,
              title: document.title,
              // eslint-disable-next-line camelcase
              non_interactive: false
            })
          )
        );
      }

      // GF 2.9+ CustomEvent
      document.addEventListener('gform/ajax/post_ajax_submission', (ev) => {
        routeConfirmation(ev.detail?.formId);
      });

      // Legacy fallback
      document.addEventListener('gform_confirmation_loaded', (ev) => {
        routeConfirmation(ev.detail?.formId);
      });

      // --- Form Start (first interaction with any form input) ---
      const startedForms = new Set();
      document.addEventListener('focusin', (ev) => {
        const input = ev.target;
        if (!input || !input.matches('input, select, textarea')) return;
        const form = input.closest('form[id^="gform_"]');
        if (!form || startedForms.has(form.id)) return;
        startedForms.add(form.id);

        analytics.track(
          'Form Start',
          prune(
            enrichProps({
              // eslint-disable-next-line camelcase
              form_id: String(form.dataset.formid || form.id.replace('gform_', '')),
              // eslint-disable-next-line camelcase
              form_name: getFormName(form),
              // eslint-disable-next-line camelcase
              form_type: getFormType(form),
              // eslint-disable-next-line camelcase
              pre_application_flow_uuid: getUuid(form) || undefined,
              // eslint-disable-next-line no-restricted-globals
              path: location.pathname,
              // eslint-disable-next-line no-restricted-globals
              url: location.href,
              title: document.title,
              // eslint-disable-next-line camelcase
              non_interactive: false
            })
          )
        );
      });

      // Populated by handlePostRender on each page render while the form is still in the DOM
      const pendingSubmit = {};

      // Captured on Next/Submit click, before GF replaces the DOM
      const pendingFormData = {};

      document.addEventListener('click', (ev) => {
        const btn = ev.target.closest(
          '[id^="gform_next_button_"], .gform_next_button, .gform_button[type="submit"], [data-submission-type="submit"]'
        );
        if (!btn) return;
        const form = btn.closest('form[id^="gform_"]');
        if (!form) return;
        const fid = String(form.dataset.formid || form.id.replace(/^gform_/, ''));
        const existing = pendingFormData[fid] ? JSON.parse(pendingFormData[fid]) : {};
        pendingFormData[fid] = JSON.stringify({ ...existing, ...capturePageData(form) });
        // eslint-disable-next-line camelcase
        if (pendingSubmit[fid]) pendingSubmit[fid].form_data = pendingFormData[fid];
      }, true);

      // Per-form Save & Continue state: the resume URL captured from the prompt
      // and the email typed into GF's native `gform_resume_email` field. Used to
      // (a) include the link in `Form Saved` and (b) fire `Saved Link Sent` with
      // link + email on the final save confirmation (which GF often renders
      // without the URL visible in the DOM).
      const saveFlowState = new Map();

      // Seed pendingSubmit for forms already on the page (initial render fires before our listener)
      for (const form of document.querySelectorAll('form[id^="gform_"]')) {
        const fid = form.dataset.formid || form.id.replace('gform_', '');
        const emailInput = form.querySelector('input[type="email"]');
        pendingSubmit[String(fid)] = {
          // eslint-disable-next-line camelcase
          form_name: getFormName(form),
          // eslint-disable-next-line camelcase
          form_type: getFormType(form),
          email: emailInput?.value?.trim() || ''
        };
      }

      // eslint-disable-next-line no-console
      console.info('[CDPAnalytics] Gravity Forms tracking initialized');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[CDPAnalytics] Gravity Forms tracking error', error);
    }
  }

  // ---------------------------
  // Debug toggle (?analytics_debug=1)
  // ---------------------------
  (function () {
    try {
      // eslint-disable-next-line no-restricted-globals
      if (location.search.includes('analytics_debug=1')) {
        try {
          localStorage.setItem('debug', 'analytics:*');
        } catch {}

        if (globalThis.analytics && typeof globalThis.analytics.debug === 'function')
          analytics.debug(true);
        // eslint-disable-next-line no-console
        console.info('[CDPAnalytics] analytics.debug(true)');
      }
    } catch {}
  })();

  // ---------------------------
  // Bootstrap everything (after analytics.ready)
  // ---------------------------
  if (globalThis.analytics) {
    analytics.ready(() => {
      onDomReady(async () => {
        // Clean + map utm_source via Edge domain BEFORE first page call
        try {
          await sourceCleaner();
        } catch {}

        fireRobustPage();
        bindTrackLink();
        bindGformTrackLink();
        wrapGlobalTrack();
        installScrollDepth();
        bindFAQTracking();
        setupVideoTracking();
        setupGravityFormsTracking();
      });
    });
  }
})();
